import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import Dropdown, {
  DropdownOptions
} from "@/components/controls/Dropdown/Dropdown";
import Input from "@/components/ui/input";
import Text, { TextVariant } from "@/components/ui/text";
import { ChevronDown } from "lucide-react";
import {
  ValidationConfig,
  commonRules,
  validateField
} from "@/utils/validation";
import DateTimeField, {
  dateTimeVariant
} from "@/components/controls/DateTimeField";
import { cn } from "@/utils/utils";
import {
  MultiSelect,
  multiSelectionOptions
} from "@/components/controls/MultiSelect";
import TextareaAutosize from "react-textarea-autosize";
import { hasPhoneNumber, isPhoneNumberValid } from "./helpers";
import React from "react";
import { useTranslation } from "react-i18next";
import SingleSelect, {
  singleSelectVariant
} from "@/components/controls/SingleSelect";
import {
  RadioOption,
  RadioGroupItem,
  RadioGroup
} from "@/components/ui/radio-group";
import RemindersField from "@/components/controls/RemindersField/RemindersField";
import AttachmentsField from "@/components/controls/AttachmentsField";
import { ArrayFieldType } from "@/entities/forms/types";
import { UserData } from "@/entities/users/types";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { forEach, isEmpty } from "lodash";

export const NONE_OPTION_VALUE = "none";

export type FieldType =
  | "text"
  | "email"
  | "multiEmail"
  | "phone"
  | "number"
  | "dropdown"
  | "multiSelect"
  | "singleSelect"
  | "date"
  | "time"
  | "textarea"
  | "radio"
  | "reminders"
  | "attachments"
  | "checkbox";

export type LayoutType = "full" | "half" | "flex";
export type LabelPosition = "top" | "left";

export interface SingleSelectOptions {
  id: string | number | null;
  displayName?: string;
  name?: string;
  subtitle?: string;
  isHidden?: boolean;
  userData?: UserData | any;
}

export interface FormField {
  name: string;
  label?: string;
  type: FieldType;
  required?: boolean;
  showRequiredIndicator?: boolean;
  options?: DropdownOptions | multiSelectionOptions | SingleSelectOptions[];
  defaultValue?: string | string[] | null;
  placeholder?: string;
  icon?: JSX.Element;
  validation?: ValidationConfig;
  onChange?: (
    // value can be string, string[], Reminder, SavedAttachmentInfo
    value: any
  ) => void;
  onBlur?: (value: string) => void;
  dependencies?: string[];
  popOverHeader?: JSX.Element;
  popOverFooter?: JSX.Element;
  layout?: LayoutType;
  includeNoneOption?: boolean;
  readOnly?: boolean;
  followingComponent?: JSX.Element;
  nearestTime?: string;
  labelPosition?: LabelPosition;
  fixedOptions?: DropdownOptions | SingleSelectOptions[];
  iconPosition?: "left" | "right";
  showSearch?: boolean;
  formatter?: (value: number | string) => string;
  renderIcon?: (value: number | string) => JSX.Element;
  onSearch?: (newSearchVal: string) => void;
  isLoading?: boolean;
  searchTerm?: string;
  entity?: EntityType;
  treatNullAsValue?: boolean;
  disabled?: boolean;
  disabledTooltipText?: string;
  className?: string;
  labelClassName?: string;
  labelVariant?: TextVariant;
  labelContainerClassName?: string;
  selectedVariant?: TextVariant;
  popOverClassName?: string;
  triggerOnlyIcon?: JSX.Element;
  singleSelectVariant?: singleSelectVariant;
  filledValueClassName?: string;
  placeholderClassName?: string;
  dateTimeVariant?: dateTimeVariant;
  isDueDate?: boolean;
  showDatePresets?: boolean;
  showOnlyNotifyAssignee?: boolean;
  shouldOnlyNotifyAssignee?: boolean;
  fieldClassName?: string;
  showSelectedItemSubtitle?: boolean;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  disabledTooltip?: string;
  labelAdornment?: React.ReactNode;
}

interface Props {
  fields: FormField[];
  isFormValid: (isValid: boolean, watchAllFields: Record<string, any>) => void;
  onSubmit?: (data: any) => void;
  formRef?: React.RefObject<HTMLFormElement>;
  onSetValue?: (setValue: any) => void;
}

export const INVALID_FIELD_BORDER_STYLE =
  "border-[0.5px] border-red-400 hover:border-red-500 hover:border-[0.5px] focus:border-[1px] focus:border-red-400 focus:bg-surface-focus-selected";

const getFormDefaultValues = (fields: FormField[]) => {
  return fields.reduce(
    (acc, field) => {
      // Handle array default values
      if ((Object.values(ArrayFieldType) as string[]).includes(field.type)) {
        acc[field.name] = field.defaultValue
          ? Array.isArray(field.defaultValue)
            ? field.defaultValue
            : [field.defaultValue]
          : [];
      } else if (
        field.type === "singleSelect" &&
        field.treatNullAsValue &&
        field.defaultValue === null
      ) {
        acc[field.name] = null;
      } else {
        acc[field.name] = field.defaultValue || "";
      }
      return acc;
    },
    {} as Record<string, any>
  );
};

const Form: React.FC<Props> = ({
  fields,
  isFormValid,
  onSubmit,
  formRef,
  onSetValue
}) => {
  const { t } = useTranslation();
  const [openDateField, setOpenDateField] = useState<string | null>(null);
  const [openTimeField, setOpenTimeField] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm({
    mode: "onSubmit",
    reValidateMode: "onBlur",
    defaultValues: getFormDefaultValues(fields)
  });

  useEffect(() => {
    if (onSetValue) {
      onSetValue({ setValue, watch });
    }
  }, [setValue, watch, onSetValue]);

  const watchAllFields = watch();

  useEffect(() => {
    // Check if all required fields are filled
    const requiredFieldsFilled = fields.every((field) => {
      if (!field.required) return true;
      const value = watchAllFields[field.name];
      if (field.type === "multiSelect") {
        return Array.isArray(value) && value.length > 0;
      }
      return value !== undefined && value !== "";
    });

    isFormValid(requiredFieldsFilled && isEmpty(errors), watchAllFields);
  }, [watchAllFields, fields]);

  const handleFormSubmit = async (data: any) => {
    try {
      if (onSubmit) {
        await onSubmit(data);
        reset();
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const getFieldValidation = (field: FormField) => {
    const validation: any = {};

    validation.validate = (value: any) => {
      const config: ValidationConfig = {
        required: field.required,
        ...field.validation
      };
      if (field.type === "multiSelect") {
        // Add validation for array type
        if (field.required && (!Array.isArray(value) || value.length === 0)) {
          return t("requiredField");
        }
      } else if (field.type === "email") {
        config.customRules = [commonRules.email(field.validation?.message)];
      } else if (field.type === "phone") {
        if (field.required && !hasPhoneNumber(value)) {
          return t("requiredField");
        }
        config.customRules = [
          {
            validate: isPhoneNumberValid,
            message: field.validation?.message || t("validation.invalidPhone")
          }
        ];
      } else if (field.type === "number") {
        config.customRules = [
          commonRules.number({
            min: field.validation?.min,
            max: field.validation?.max
          })
        ];
      }

      const error = validateField(value, config, watch());
      return error || true;
    };

    return validation;
  };

  const renderField = (field: FormField, hasError: boolean) => {
    switch (field.type) {
      case "radio":
        return (
          <RadioGroup
            value={watch(field.name)}
            onValueChange={(value) => {
              setValue(field.name, value, {
                shouldValidate: true
              });
              field.onChange?.(value);
            }}
            defaultValue={(field?.defaultValue as string) || ""}
            className={cn("gap-0", hasError ? "text-red-500" : "")}
            // Both states lock the group; forwarding only readOnly left a
            // disabled radio field editable.
            disabled={field.disabled || field.readOnly}
          >
            {(field.options as RadioOption[])?.map((option) => (
              <div key={option.value} className="flex items-center space-x-2">
                <div className="size-8 flex items-center justify-center">
                  <RadioGroupItem
                    value={option.value}
                    id={`${field.name}-${option.value}`}
                  />
                </div>
                <div>
                  {option.label && (
                    <label htmlFor={`${field.name}-${option.value}`}>
                      <Text variant="body">{option.label}</Text>
                    </label>
                  )}
                  {option.description && (
                    <Text variant="bodySecondary">{option.description}</Text>
                  )}
                </div>
              </div>
            ))}
          </RadioGroup>
        );
      case "singleSelect":
        const fieldValue = watch(field.name);
        const processedValue =
          fieldValue === "" &&
          field.treatNullAsValue &&
          field.defaultValue === null
            ? null
            : fieldValue;
        return (
          <SingleSelect
            fieldName={field.name}
            options={field.options as SingleSelectOptions[]}
            fixedOptions={field.fixedOptions as SingleSelectOptions[]}
            selectedValue={processedValue}
            placeholder={field?.placeholder}
            onSelection={(value) => {
              setValue(field.name, value, {
                shouldValidate: true
              });
              if (!isEmpty(field?.dependencies)) {
                forEach(field?.dependencies, (dependency) => {
                  setValue(dependency, "");
                });
              }
              field.onChange?.(value !== null ? value.toString() : value);
            }}
            formatter={field.formatter}
            renderIcon={field.renderIcon}
            showSearch={field.showSearch}
            variant={field?.singleSelectVariant || "form"}
            clearable={!field.required}
            onClear={() => {
              setValue(field.name, "", {
                shouldValidate: true
              });
              field.onChange?.("");
            }}
            className={cn("pl-0", field?.fieldClassName)}
            popoverTriggerClassName={field?.className}
            popOverFooter={field?.popOverFooter}
            popOverHeader={field?.popOverHeader}
            onSearch={field?.onSearch}
            isLoading={field?.isLoading}
            initialSearchTerm={field?.searchTerm}
            treatNullAsValue={field?.treatNullAsValue}
            disabled={field?.disabled}
            readOnly={field?.readOnly}
            disabledTooltipText={field?.disabledTooltipText}
            selectedVariant={field?.selectedVariant}
            popoverClassName={field?.popOverClassName}
            triggerOnlyIcon={field?.triggerOnlyIcon}
            filledValueClassName={field?.filledValueClassName}
            placeholderClassName={field?.placeholderClassName}
            showSelectedItemSubtitle={field?.showSelectedItemSubtitle}
            isOpen={field?.isOpen}
            onOpenChange={field?.onOpenChange}
          />
        );
      case "multiSelect":
        return (
          <MultiSelect
            selectedValues={watch(field.name)}
            options={(field?.options as multiSelectionOptions) || []}
            onValueChange={(newValues: string[]) => {
              setValue(field.name, newValues, {
                shouldValidate: true
              });
              field?.onChange?.(newValues);
            }}
            showIconInChip
            modalPopover
            placeholder={field?.placeholder}
            maxCount={5}
            className={cn(hasError && INVALID_FIELD_BORDER_STYLE)}
            icon={field?.icon}
            includeNoneOption={field?.includeNoneOption}
            // MultiSelect gates interaction on readOnly || disabled, so
            // forwarding only readOnly left a disabled field selectable.
            disabled={field?.disabled}
            readOnly={field?.readOnly}
            showSearch={field?.showSearch}
            onSearch={field?.onSearch}
            isLoading={field?.isLoading}
            entity={field?.entity}
            formatter={field?.formatter}
          />
        );
      case "dropdown":
        return (
          <Dropdown
            dataTest={`${field.name}-dropdown`}
            className={cn(
              "p-2 pl-3 ",
              hasError
                ? INVALID_FIELD_BORDER_STYLE
                : "border border-border-border2 hover:border-border-border2",
              field.labelPosition !== "left" ? "w-[55vh]" : "w-full"
            )}
            valueSelected={(value) => {
              setValue(field.name, value, {
                shouldValidate: true
              });
              if (!isEmpty(field?.dependencies)) {
                forEach(field?.dependencies, (dependency) => {
                  setValue(dependency, "");
                });
              }
              field.onChange && field.onChange(value);
            }}
            placeholder={field?.placeholder || ""}
            options={(field?.options as DropdownOptions) || {}}
            defaultValue={(field?.defaultValue as string) || ""}
            value={watch(field.name)}
            popOverFooter={field?.popOverFooter}
            customIcon={
              <ChevronDown className="h-4 cursor-pointer text-secondary transition-transform duration-200 group-data-[state=open]:rotate-180 group-data-[state=closed]:rotate-0" />
            }
            selectedOptionClassName="text-primary"
            menuItemClassName="text-primary"
            // Both lock the control; forwarding only readOnly left a disabled
            // dropdown selectable.
            disabled={field?.disabled}
            readOnly={field?.readOnly}
            fixedOptions={field?.fixedOptions as DropdownOptions}
          />
        );
      case "time":
      case "date":
        return (
          <DateTimeField
            {...register(field.name, getFieldValidation(field))}
            type={field.type}
            value={watch(field.name)}
            onChange={(value) => {
              setValue(field.name, value, {
                shouldValidate: true
              });
              field.onChange?.(value);
            }}
            placeholder={field?.placeholder}
            isOpen={
              field.type === "date"
                ? openDateField === field.name
                : openTimeField === field.name
            }
            onOpenChange={(open) => {
              if (field.type === "date") {
                setOpenDateField(open ? field.name : null);
              } else {
                setOpenTimeField(open ? field.name : null);
              }
            }}
            name={field.name}
            nearestTime={field?.nearestTime}
            className={cn(
              field.type === "time" && field.layout === "half" && "w-48",
              hasError
                ? INVALID_FIELD_BORDER_STYLE
                : "border border-border-border2"
            )}
            iconPosition={field?.iconPosition}
            // DateTimeField has no readOnly of its own; disabled is its
            // read-only presentation and is what stops the picker opening.
            // Same shape the radio branch already uses.
            disabled={field?.disabled || field?.readOnly}
            // The clear control lives inside DateTimeField, so required has to
            // reach it as a prop — there is no onClear here to withhold.
            required={field?.required}
            variant={field?.dateTimeVariant || "form"}
            isDueDate={field?.isDueDate}
            showDatePresets={field?.showDatePresets}
          />
        );
      case "reminders":
        return (
          <RemindersField
            {...register(field.name, getFieldValidation(field))}
            value={watch(field.name)}
            onChange={(value) => {
              setValue(field.name, value, {
                shouldValidate: true
              });
              field.onChange?.(value);
            }}
            onUpdateOnlyNotifyAssignee={(value) => {
              setValue("shouldOnlyNotifyAssignee", value, {
                shouldValidate: true
              });
            }}
            placeholder={field?.placeholder}
            name={field.name}
            shouldOnlyNotifyAssignee={field?.shouldOnlyNotifyAssignee}
            showOnlyNotifyAssignee={field?.showOnlyNotifyAssignee}
            // No readOnly prop; disabled is the only lock it offers.
            disabled={field?.disabled || field?.readOnly}
            disabledTooltip={field?.disabledTooltip}
          />
        );
      case "attachments":
        return (
          <AttachmentsField
            {...register(field.name, getFieldValidation(field))}
            // Forms are the design's edit mode: the field owns a block of its
            // own here, so it opens on the Large drag-and-drop area rather than
            // on the record row's compact upload button.
            variant="edit"
            value={watch(field.name)}
            onChange={(value) => {
              setValue(field.name, value, {
                shouldValidate: true
              });
              field.onChange?.(value);
            }}
            placeholder={field?.placeholder}
            name={field.name}
            // No readOnly prop either — it derives isReadOnly from disabled.
            disabled={field?.disabled || field?.readOnly}
            className={field?.className}
          />
        );
      case "textarea":
        return (
          <TextareaAutosize
            {...register(field.name, getFieldValidation(field))}
            value={watch(field.name)}
            minRows={6}
            disabled={field?.disabled}
            readOnly={field?.readOnly}
            placeholder={field?.placeholder || ""}
            onBlur={(e) => {
              register(field.name).onBlur(e);
              field.onBlur?.(e.target.value);
            }}
            className={cn(
              "w-full text-base font-normal text-primary placeholder:text-tertiary px-3 py-[6px] bg-surface-field-default rounded-lg resize-none shadow-field outline-none transition-all duration-200 ease-in-out",
              hasError
                ? INVALID_FIELD_BORDER_STYLE
                : "border-[0.5px] border-border-field-default hover:border-border-field-hover focus:border-border-field-focus focus:bg-surface-focus-selected focus:shadow-field-focus",
              field?.className
            )}
          />
        );
      default: {
        // Both handlers must chain into the registration: overriding onChange
        // with the field's own callback drops the value on the floor until blur,
        // and blur alone never re-renders the form to republish validity.
        const registration = register(field.name, getFieldValidation(field));
        return (
          <Input
            {...registration}
            data-test={`${field.name}-input`}
            type={field.type}
            className={cn(
              "w-full",
              hasError ? INVALID_FIELD_BORDER_STYLE : "border-border-border2",
              field?.className
            )}
            placeholder={field?.placeholder || ""}
            leadingIcon={field?.icon}
            onBlur={(e) => {
              registration.onBlur(e);
              field.onBlur?.(e.target.value);
            }}
            onChange={(e) => {
              registration.onChange(e);
              field?.onChange?.(e.target.value);
            }}
          />
        );
      }
    }
  };

  const renderFieldWithLabel = (field: FormField) => {
    const showLabel = field.showRequiredIndicator !== false;
    const labelPosition = field.labelPosition || "top";
    const labelClassName = cn(
      "flex shrink-0 items-center",
      labelPosition === "left" && "w-28 pt-2",
      field?.labelClassName
    );
    const labelContent = (
      <>
        <Text
          data-test={`${field.name}-label`}
          variant={field?.labelVariant || "bodySecondary"}
        >
          {field.label}
        </Text>
        {field.required && <span className="text-primary ml-1">*</span>}
      </>
    );

    return (
      <div
        className={cn(
          field.layout === "flex"
            ? "w-auto min-w-fit"
            : field.layout === "half"
              ? "flex-1"
              : "w-full",
          labelPosition === "left" ? "flex items-start gap-4" : "space-y-2",
          field?.labelContainerClassName
        )}
      >
        {showLabel &&
          (field.labelAdornment ? (
            // Beside the label, not inside it: a label click activates the first button it contains.
            <div className={labelClassName}>
              <label className="flex items-center">{labelContent}</label>
              <span className="ml-2">{field.labelAdornment}</span>
            </div>
          ) : (
            <label className={labelClassName}>{labelContent}</label>
          ))}
        <div className="flex-1">
          {renderField(field, field.name in errors)}
          {errors[field.name] && (
            <span className={cn("text-sm text-red-500")}>
              {errors[field.name]?.message as string}
            </span>
          )}
        </div>
      </div>
    );
  };

  const groupedFields = fields.reduce((acc: FormField[][], field) => {
    const lastGroup = acc[acc.length - 1] || [];

    if (field.layout === "half") {
      if (lastGroup.length === 1 && lastGroup[0].layout === "half") {
        lastGroup.push(field);
      } else {
        acc.push([field]);
      }
    } else if (field.layout === "flex") {
      if (lastGroup.length > 0 && lastGroup[0].layout === "flex") {
        lastGroup.push(field);
      } else {
        acc.push([field]);
      }
    } else {
      acc.push([field]);
    }

    return acc;
  }, []);

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit(handleFormSubmit)}
      className="space-y-5"
    >
      {groupedFields.map((group, groupIndex) => (
        <React.Fragment key={`group-${groupIndex}`}>
          <div
            className={cn(
              "gap-4",
              group.length > 0 &&
                group.every((field) => field.layout === "flex")
                ? "flex items-end"
                : group.length > 1 && group[0].layout === "half"
                  ? "flex"
                  : "block"
            )}
          >
            {group.map((field: FormField, index) => (
              <React.Fragment key={`field-${field.name}-${index}`}>
                {renderFieldWithLabel(field)}
              </React.Fragment>
            ))}
          </div>
          {group.some((field) => field.followingComponent) && (
            <div className="w-full">
              {
                group.find((field) => field.followingComponent)
                  ?.followingComponent
              }
            </div>
          )}
        </React.Fragment>
      ))}
    </form>
  );
};

export default Form;
